import { EventEmitter } from 'eventemitter3';
import { TUNING, type Side } from '../config/tuning';
import { JointController } from './JointController';
import { LungController } from './LungController';
import { ChokeController } from './ChokeController';
import { ScoreController } from './ScoreController';
import type { IAIController } from './AIController';

export type TurnState =
  | 'coin-flip'
  | 'idle-player'
  | 'inhaling-player'
  | 'combo-window-player'
  | 'idle-ai'
  | 'inhaling-ai'
  | 'combo-window-ai'
  | 'passing'
  | 'choking'
  | 'game-over';

export interface TurnContext {
  joint: JointController;
  lung: LungController;
  choke: ChokeController;
  score: ScoreController;
  ai: IAIController;
}

export interface InhaleEndDetails {
  side: Side;
  seconds: number;
  banked: boolean;
  reason: 'released' | 'choked' | 'joint-burned' | 'lung-empty';
}

export class TurnController extends EventEmitter {
  static EVT_STATE = 'turn:state';
  static EVT_INHALE_START = 'turn:inhale-start';
  static EVT_INHALE_END = 'turn:inhale-end';
  static EVT_COMBO_WINDOW_START = 'turn:combo-window-start';
  static EVT_COMBO_WINDOW_END = 'turn:combo-window-end';
  static EVT_PASS_START = 'turn:pass-start';
  static EVT_PASS_END = 'turn:pass-end';
  static EVT_GAME_OVER = 'turn:game-over';
  static EVT_TURN_START = 'turn:turn-start';

  private state: TurnState = 'coin-flip';
  private ctx: TurnContext;
  private currentDragSecs = 0;
  private comboTimerMs = 0;
  private passTimerMs = 0;
  private chokeTimerMs = 0;
  private nextTurnSide: Side = 'player';
  private playerHoldingSpace = false;
  private playerSpacePressedDuringIdle = false;
  private currentTurnSide: Side = 'player';

  /**
   * Seconds banked so far IN THE CURRENT TURN (across all successful drags in the combo).
   * Banked for real only when the turn passes cleanly; forfeited entirely if the player
   * chokes, burns the cardboard, or empties their lungs at any point during the turn.
   */
  private turnBankedSecs = 0;
  private turnLongestDrag = 0;
  private turnDragCount = 0;

  constructor(ctx: TurnContext) {
    super();
    this.ctx = ctx;
  }

  /** Called once after coin flip animation finishes. */
  beginMatch(firstSide: Side): void {
    this.ctx.choke.reset();
    this.resetTurnBuffer();
    this.currentTurnSide = firstSide;
    this.transition(firstSide === 'player' ? 'idle-player' : 'idle-ai');
    this.emit(TurnController.EVT_TURN_START, firstSide);
    if (firstSide === 'ai') this.ctx.ai.beginTurn();
  }

  /** Forwarded from key handler. */
  onSpaceDown(): void {
    if (this.state === 'idle-player') {
      this.playerSpacePressedDuringIdle = true;
      this.startInhale('player');
    } else if (this.state === 'combo-window-player') {
      this.startInhale('player');
    }
    this.playerHoldingSpace = true;
  }

  onSpaceUp(): void {
    this.playerHoldingSpace = false;
    if (this.state === 'inhaling-player') {
      this.endInhale('player', 'released');
    }
  }

  update(deltaMs: number): void {
    const dt = deltaMs / 1000;
    switch (this.state) {
      case 'inhaling-player':
        this.tickInhale('player', dt);
        break;
      case 'inhaling-ai':
        this.tickInhale('ai', dt);
        this.tickAi(deltaMs);
        break;
      case 'combo-window-player':
        this.tickComboWindow('player', deltaMs);
        break;
      case 'combo-window-ai':
        this.tickComboWindow('ai', deltaMs);
        this.tickAi(deltaMs);
        break;
      case 'idle-ai':
        this.tickAi(deltaMs);
        break;
      case 'idle-player':
        this.ctx.lung.recover('player', 'idle', dt);
        this.ctx.lung.recover('ai', 'idle', dt);
        break;
      case 'passing':
        this.passTimerMs -= deltaMs;
        this.ctx.lung.recover('player', 'idle', dt);
        this.ctx.lung.recover('ai', 'idle', dt);
        if (this.passTimerMs <= 0) this.completePass();
        break;
      case 'choking':
        this.chokeTimerMs -= deltaMs;
        this.ctx.lung.recover('player', 'idle', dt * 0.5);
        this.ctx.lung.recover('ai', 'idle', dt * 0.5);
        if (this.chokeTimerMs <= 0) this.startPassAfterChoke();
        break;
    }
  }

  getState(): TurnState {
    return this.state;
  }

  getCurrentDragSecs(): number {
    return this.currentDragSecs;
  }

  /** Seconds banked in this turn's combo so far (cleared on turn-pass / forfeit). */
  getTurnBankedSecs(): number {
    return this.turnBankedSecs;
  }

  /** Total at-risk seconds: turn-combo accumulation + current in-progress drag. */
  getTurnAtRiskSecs(): number {
    return this.turnBankedSecs + this.currentDragSecs;
  }

  getActiveSide(): Side | null {
    if (this.state.startsWith('inhaling-') || this.state.startsWith('combo-window-') || this.state.startsWith('idle-')) {
      return this.state.endsWith('player') ? 'player' : 'ai';
    }
    return null;
  }

  getTurnDragCount(): number {
    // If inhaling, we're on the next count. If in combo window, we've finished 'turnDragCount' drags.
    const inhaling = this.state === 'inhaling-player' || this.state === 'inhaling-ai';
    return inhaling ? this.turnDragCount + 1 : this.turnDragCount;
  }

  // -- Internals --

  private transition(next: TurnState): void {
    this.state = next;
    this.emit(TurnController.EVT_STATE, next);
  }

  private startInhale(side: Side): void {
    this.currentDragSecs = 0;
    this.transition(side === 'player' ? 'inhaling-player' : 'inhaling-ai');
    // NOTE: choke meter does NOT reset between drags within a combo. It accumulates
    // across the whole turn and only resets when the joint passes to the other side.
    this.emit(TurnController.EVT_INHALE_START, side);
    if (side === 'ai') this.ctx.ai.notifyInhaleStarted();
    if (this.state === 'combo-window-player' || this.state === 'combo-window-ai') {
      this.emit(TurnController.EVT_COMBO_WINDOW_END, side);
    }
  }

  private endInhale(side: Side, reason: 'released' | 'choked' | 'joint-burned' | 'lung-empty'): void {
    const seconds = this.currentDragSecs;
    const banked = reason === 'released';
    if (banked) {
      // Don't bank to score yet — accumulate to the turn buffer. The combo banks
      // for real only when the turn passes cleanly. A later forfeit in the same
      // combo wipes everything out.
      this.turnBankedSecs += seconds;
      this.turnDragCount += 1;
      if (seconds > this.turnLongestDrag) this.turnLongestDrag = seconds;
    }
    this.currentDragSecs = 0;
    const details: InhaleEndDetails = { side, seconds, banked, reason };
    this.emit(TurnController.EVT_INHALE_END, details);

    if (side === 'ai') this.ctx.ai.notifyInhaleEnded(reason);

    if (this.ctx.joint.isDepleted()) {
      // The joint ran out. If this drag was a forfeit (cardboard burn / choke / lung-empty),
      // the whole combo is forfeited. Otherwise the cleanly-released drag's combo banks normally.
      if (reason === 'released') {
        this.commitTurnBank(side);
      } else {
        this.commitTurnForfeit(side, seconds);
      }
      this.endGame();
      return;
    }

    if (reason === 'released') {
      this.startComboWindow(side);
    } else {
      // Forfeit ENTIRE turn buffer (every drag in this combo is wiped).
      this.commitTurnForfeit(side, seconds);
      this.transition('choking');
      this.chokeTimerMs = TUNING.turn.chokeAnimationMs;
    }
  }

  /** Bank everything accumulated this turn to the player's score. */
  private commitTurnBank(side: Side): void {
    if (this.turnBankedSecs > 0) {
      this.ctx.score.bankTurn(side, this.turnBankedSecs, this.turnLongestDrag, this.turnDragCount);
    }
    this.resetTurnBuffer();
  }

  /** Forfeit everything accumulated this turn (combo wipe). */
  private commitTurnForfeit(side: Side, currentDragSeconds: number): void {
    const lost = this.turnBankedSecs + currentDragSeconds;
    this.ctx.score.forfeitTurn(side, lost);
    this.resetTurnBuffer();
  }

  private resetTurnBuffer(): void {
    this.turnBankedSecs = 0;
    this.turnLongestDrag = 0;
    this.turnDragCount = 0;
  }

  private startComboWindow(side: Side): void {
    this.comboTimerMs = TUNING.combo.windowMs;
    this.transition(side === 'player' ? 'combo-window-player' : 'combo-window-ai');
    this.emit(TurnController.EVT_COMBO_WINDOW_START, side, this.comboTimerMs);
  }

  private tickInhale(side: Side, dt: number): void {
    this.currentDragSecs += dt;
    const lungEmpty = this.ctx.lung.drainInhale(side, dt, this.ctx.joint.getLengthPct());
    const burned = this.ctx.joint.consume(dt);
    const choked = this.ctx.choke.tickInhale(side, dt);
    if (burned) {
      this.endInhale(side, 'joint-burned');
      return;
    }
    if (choked) {
      this.endInhale(side, 'choked');
      return;
    }
    if (lungEmpty) {
      this.endInhale(side, 'lung-empty');
    }
  }

  private tickComboWindow(side: Side, deltaMs: number): void {
    this.comboTimerMs -= deltaMs;
    this.ctx.lung.recover(side, 'combo', deltaMs / 1000);
    const otherSide: Side = side === 'player' ? 'ai' : 'player';
    this.ctx.lung.recover(otherSide, 'idle', deltaMs / 1000);
    if (this.comboTimerMs <= 0) {
      this.emit(TurnController.EVT_COMBO_WINDOW_END, side);
      this.startPass(side);
    }
  }

  private tickAi(deltaMs: number): void {
    if (this.state !== 'idle-ai' && this.state !== 'inhaling-ai' && this.state !== 'combo-window-ai') return;
    const intent = this.ctx.ai.update(deltaMs, {
      lungPct: this.ctx.lung.get('ai'),
      chokePct: this.ctx.choke.get('ai'),
      jointPct: this.ctx.joint.getLengthPct(),
      myScore: this.ctx.score.getTotal('ai'),
      opponentScore: this.ctx.score.getTotal('player'),
      turnBankedSecs: this.turnBankedSecs
    });
    if (this.state === 'idle-ai' && intent === 'start-inhale') {
      this.startInhale('ai');
    } else if (this.state === 'inhaling-ai' && intent === 'end-inhale') {
      this.endInhale('ai', 'released');
    } else if (this.state === 'combo-window-ai') {
      if (intent === 'start-inhale') this.startInhale('ai');
      else if (intent === 'pass') {
        this.emit(TurnController.EVT_COMBO_WINDOW_END, 'ai');
        this.startPass('ai');
      }
    }
  }

  private startPass(fromSide: Side): void {
    // A clean pass commits the turn's combo to the score. (Forfeits committed earlier
    // already wiped the buffer; this branch handles the normal-end case.)
    this.commitTurnBank(fromSide);
    this.nextTurnSide = fromSide === 'player' ? 'ai' : 'player';
    this.currentTurnSide = this.nextTurnSide; // Track the new side
    this.passTimerMs = TUNING.turn.passAnimationMs;
    this.transition('passing');
    this.emit(TurnController.EVT_PASS_START, fromSide, this.nextTurnSide);
  }

  private startPassAfterChoke(): void {
    const choker = this.currentTurnSide;
    // Choke already forfeited the turn buffer; this just animates the joint handoff.
    this.nextTurnSide = choker === 'player' ? 'ai' : 'player';
    this.currentTurnSide = this.nextTurnSide; // Track the new side
    this.passTimerMs = TUNING.turn.passAnimationMs;
    this.transition('passing');
    this.emit(TurnController.EVT_PASS_START, choker, this.nextTurnSide);
  }

  private completePass(): void {
    this.emit(TurnController.EVT_PASS_END, this.nextTurnSide);
    if (this.ctx.joint.isDepleted()) {
      this.endGame();
      return;
    }
    // New turn — full reset for both sides. Whoever just finished their turn
    // gets their lungs and choke cleared so they're ready to receive next
    // time; whoever is about to take a turn also starts fresh. This gives a
    // clean slate at every handoff and removes the slow-drip recovery that
    // was leaving players choked and winded well into the next turn.
    this.ctx.choke.reset();
    this.ctx.lung.reset();
    this.resetTurnBuffer();
    if (this.nextTurnSide === 'ai') {
      this.transition('idle-ai');
      this.ctx.ai.beginTurn();
    } else {
      this.transition('idle-player');
      this.ctx.ai.endTurn();
    }
    this.emit(TurnController.EVT_TURN_START, this.nextTurnSide);
  }

  private endGame(): void {
    this.transition('game-over');
    this.emit(TurnController.EVT_GAME_OVER);
  }

  isPlayerHoldingSpace(): boolean {
    return this.playerHoldingSpace;
  }

  consumePlayerSpacePressedFlag(): boolean {
    const v = this.playerSpacePressedDuringIdle;
    this.playerSpacePressedDuringIdle = false;
    return v;
  }
}
